/**
 * One cached copy of `GET /api/onboarding` for the whole signed-in app.
 *
 * The Dashboard checklist/banners and the Documents upload gate both need it, so the provider sits
 * inside the authenticated layout and they share a single request. While provisioning is still in
 * flight the provider re-polls on a timer, with a hard attempt budget so a stuck tenant cannot
 * leave the browser polling forever.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './auth';
import { setTenantNotVerifiedHandler } from './api';
import { onboarding as onboardingApi } from './endpoints';
import { TenantFeatureAccess, TenantProvisioningStatus } from './enums';
import type { TenantOnboardingDto } from './types';

/** Poll cadence while provisioning is Pending or Registering. */
export const ONBOARDING_POLL_MS = 10_000;

/** Stop polling after this many refreshes even if provisioning has not finished. */
export const ONBOARDING_MAX_POLLS = 20;

/** Ignore repeat refresh triggers (gate 403s, focus events) arriving within this window. */
export const ONBOARDING_REFRESH_THROTTLE_MS = 5_000;

/** A tab regaining visibility refreshes the state only when it is at least this stale. */
export const ONBOARDING_STALE_MS = 60_000;

export interface OnboardingState {
  data: TenantOnboardingDto | null;
  loading: boolean;
  error: string | null;
  /** Re-fetches now and restarts the poll budget. */
  reload: () => void;
  /** True while the provisioning poll is still armed. */
  polling: boolean;
  /** True when provisioning never finished within the attempt budget. */
  pollExhausted: boolean;
}

const OnboardingContext = createContext<OnboardingState | null>(null);

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

function isProvisioning(data: TenantOnboardingDto | null): boolean {
  if (!data) return false;
  return (
    data.provisioningStatus === TenantProvisioningStatus.Pending ||
    data.provisioningStatus === TenantProvisioningStatus.Registering
  );
}

export function OnboardingProvider({ children }: { children: ReactNode }): JSX.Element {
  const { isAuthenticated, user } = useAuth();
  const tenantId = user?.tenantId ?? null;

  const [data, setData] = useState<TenantOnboardingDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [polls, setPolls] = useState(0);

  const lastLoadedAt = useRef(0);
  const lastTriggeredAt = useRef(0);

  // Set by reload(): the next fetch asks the server to poll the Registration Server first, so a
  // person's Check again (and the gate-403 / tab-visibility refreshes) report live truth. The
  // background provisioning poll below never sets it — steady polling stays the worker's job.
  const wantServerRefresh = useRef(false);

  // Never call the authenticated endpoint without a session.
  useEffect(() => {
    if (!isAuthenticated) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let active = true;
    setLoading(true);

    const serverRefresh = wantServerRefresh.current;
    wantServerRefresh.current = false;

    onboardingApi
      .get(controller.signal, serverRefresh)
      .then((result) => {
        if (!active) return;
        setData(result);
        setError(null);
        lastLoadedAt.current = Date.now();
      })
      .catch((cause: unknown) => {
        if (!active) return;
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(messageOf(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [isAuthenticated, tenantId, nonce, polls]);

  // A different tenant (or a fresh sign-in) gets a fresh poll budget.
  useEffect(() => {
    setPolls(0);
  }, [tenantId, isAuthenticated]);

  useEffect(() => {
    if (!isProvisioning(data) || polls >= ONBOARDING_MAX_POLLS) return;
    const timer = window.setTimeout(() => setPolls((count) => count + 1), ONBOARDING_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [data, polls]);

  const reload = useCallback(() => {
    wantServerRefresh.current = true;
    setPolls(0);
    setNonce((value) => value + 1);
  }, []);

  /** Reload, but ignore bursts — a locked page can fire several gate 403s in one render. */
  const reloadThrottled = useCallback(() => {
    const now = Date.now();
    if (now - lastTriggeredAt.current < ONBOARDING_REFRESH_THROTTLE_MS) return;
    lastTriggeredAt.current = now;
    reload();
  }, [reload]);

  // Any API call refused by the server's licence gate means this cached state is stale — the
  // licence lapsed or the account was suspended after it loaded — so re-fetch and let the UI fall
  // back to the locked screens. The inverse (reinstatement) is covered by the visibility refresh.
  useEffect(() => {
    if (!isAuthenticated) return;
    setTenantNotVerifiedHandler(reloadThrottled);
    return () => setTenantNotVerifiedHandler(null);
  }, [isAuthenticated, reloadThrottled]);

  // A tab that comes back after sitting in the background re-checks a stale state, so a licence
  // lapse — or a reinstatement — shows up without anyone hammering refresh.
  useEffect(() => {
    if (!isAuthenticated) return;

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadedAt.current < ONBOARDING_STALE_MS) return;
      reloadThrottled();
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated, reloadThrottled]);

  const value = useMemo<OnboardingState>(
    () => ({
      data,
      loading,
      error,
      reload,
      polling: isProvisioning(data) && polls < ONBOARDING_MAX_POLLS,
      pollExhausted: isProvisioning(data) && polls >= ONBOARDING_MAX_POLLS,
    }),
    [data, loading, error, reload, polls],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding(): OnboardingState {
  const context = useContext(OnboardingContext);
  if (!context) throw new Error('useOnboarding must be used inside <OnboardingProvider>.');
  return context;
}

/**
 * The SPA's single feature-gate predicate. It does not re-implement the licensing rule: the server
 * computes `featureAccess` with the SAME `TenantVerificationFilter.Evaluate` the API gate enforces
 * (including tenant deactivation, which the DTO does not otherwise expose), and this function only
 * translates that value for display:
 *
 *   - `unknown`          — onboarding state not loaded (or the request failed): fail closed
 *   - `suspended`        — iERP has suspended or deactivated the account: contact iERP
 *   - `unverified`       — provisioning has not reached Ready: verification email / resend
 *   - `licence-inactive` — Ready, but the refreshed licence is reported inactive: contact iERP
 *   - `enabled`          — the server says features are allowed
 *
 * If the licensing rule changes server-side, this mapping keeps working untouched — the one thing
 * the client decides for itself is that "not loaded" means locked.
 */
export type TenantFeatureState =
  | 'unknown'
  | 'suspended'
  | 'unverified'
  | 'licence-inactive'
  | 'enabled';

export function tenantFeatureState(data: TenantOnboardingDto | null): TenantFeatureState {
  if (data === null) return 'unknown';

  switch (data.featureAccess) {
    case TenantFeatureAccess.Enabled:
      return 'enabled';
    case TenantFeatureAccess.LicenceInactive:
      return 'licence-inactive';
    case TenantFeatureAccess.Suspended:
      return 'suspended';
    default:
      return 'unverified';
  }
}

/**
 * True only once the onboarding state has loaded AND the tenant passes the same predicate the API
 * enforces. Unknown (still loading, or the request failed) counts as NOT verified: for an
 * accounting product the gate fails closed. The API enforces the same rule server-side either way.
 */
export function useTenantVerified(): boolean {
  const { data } = useOnboarding();
  return tenantFeatureState(data) === 'enabled';
}

/**
 * Wraps a feature page. The page renders only once the onboarding state has loaded and says the
 * licence is verified; while the state is unknown the person sees a loading strip, and a failed
 * state check fails closed with a retry. Mirrors the server-side gate — the API would refuse the
 * calls anyway, this just says why before a wall of failed requests does.
 */
export function RequireVerifiedTenant({ children }: { children: ReactNode }): JSX.Element | null {
  const { data, loading, error, reload } = useOnboarding();
  const state = tenantFeatureState(data);

  if (state === 'unknown') {
    if (loading) {
      return (
        <div className="banner banner-neutral" role="status" aria-live="polite">
          <div className="banner-body">
            <p className="banner-title">
              <span className="spinner-ring" aria-hidden="true" />
              Checking your account state…
            </p>
          </div>
        </div>
      );
    }

    // The check itself failed. Unknown is locked, not open — but it must offer a way forward.
    return (
      <div className="banner banner-warn" role="status">
        <div className="banner-body">
          <p className="banner-title">Your account state could not be confirmed</p>
          <p className="banner-detail">
            {error ?? 'The gateway could not read your account state.'} This area stays locked until
            the check succeeds.
          </p>
        </div>
        <div className="banner-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={reload}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (state === 'enabled') return <>{children}</>;

  // Locked, and the reason changes what the person should do: an unverified registration has a
  // self-service fix; a lapsed licence or a suspension needs iERP. Same split the API's 403 makes.
  const copy =
    state === 'licence-inactive'
      ? {
          title: 'This area is locked while the licence is inactive',
          detail:
            'The iERP AI licence for this account is not active. Contact iERP to reinstate it; ' +
            'everything already posted stays exactly as it is.',
        }
      : state === 'suspended'
        ? {
            title: 'This account is suspended',
            detail:
              'iERP has suspended this account. Contact iERP to have it reinstated; everything ' +
              'already posted stays exactly as it is.',
          }
        : {
            title: 'This area unlocks after your licence is verified',
            detail:
              'Your iERP AI licence has not been verified yet, so the gateway’s features are ' +
              'locked. Open the verification email iERP sent — or resend it from the dashboard — ' +
              'then come back.',
          };

  return (
    <div className="banner banner-warn" role="status">
      <div className="banner-body">
        <p className="banner-title">{copy.title}</p>
        <p className="banner-detail">{copy.detail}</p>
      </div>
    </div>
  );
}
